/**
 * CypAdhdSsiSymptomPanel — one DSM-5 symptom, laid out as on the adult SSI.
 *
 * The CARD is transcribed from the adult SSI symptom panel: the symptom
 * wording as a heading with its clinical tooltip, then a single gray-50
 * rounded-2xl card. Its body follows the order of the clinical content doc
 * (October 2026 revision):
 *
 *   1. the Yes/No decisions along the top (the six-month "symptom present"
 *      for every child, and "before age 12" as well once the child is older
 *      than 12). The short symptom name is not repeated here: the long
 *      wording is already the heading above the card;
 *   2. the school's SNAP-IV response on the left (the parent/carer's
 *      SNAP-IV is not shown during the interview — it stays on the patient
 *      overview and the MDT review). Suggested prompts stay in the content
 *      and are not shown;
 *   3. on the right, Home and Education as setting chips, then the two
 *      example checklists side by side, then the one clinical notes box.
 *      There is no Yes/No, "Other" line or notes box inside a setting.
 *
 * NAVIGATION IS NOT HERE. The adult ADHD panel carries its own Previous/Next
 * buttons inside the scrolling content; this page uses the fixed footer
 * (ModularStepFooter) that the TOC autism modules use, per Sai's design
 * direction of 26 Aug. That also satisfies the standing rule that the primary
 * action never needs scrolling to reach — with eighteen symptoms, in-panel
 * buttons sit below a long card and move every time the content changes height.
 *
 * TWO TEMPORAL QUESTIONS, NO GENERIC "IS IT PRESENT" (decision, 26 Aug).
 * The adult card asks "Symptom present?" once per life period, so presence
 * there IS the temporal question. A child has one life period, so the same
 * two questions become "persisted for at least 6 months" (every age) and
 * "present before age 12" (13 and over, or every age in onset-pair mode). A
 * symptom counts toward Criterion A by the six-month answer being Yes.
 *
 * Both are answered Yes or No. Clicking the button that is already on
 * clears it, so an accidental click is not stuck. A record saved earlier
 * as Inconclusive still shows that answer so it can be seen and cleared;
 * the option is not offered on a blank question.
 */

import React from 'react';
import InfoTooltip from '../../../../components/InfoTooltip';
import StructuredNotesEditor from '../../12_observation/components/StructuredNotesEditor';
import { CYP_NOTE_TAGGING_ENABLED, CYP_NOTE_TAG_OPTIONS } from '../../../../constants/cypAdhdNoteTagOptions';
import { resolveCypSnapEvidence } from '../../../../utils/cypSnapEvidence';
import {
  CYP_SSI_ENVIRONMENTS,
  CYP_SSI_INCONCLUSIVE,
} from '../config/cypAdhdSsiConfig';

// Suggested prompts stay authored on each symptom so they can be shown again
// without being rewritten. They are hidden for now.
const SHOW_SUGGESTED_PROMPTS = false;

/**
 * One informant's SNAP-IV response for this symptom, in the adult SSI's
 * evidence language: source label, instrument badge, "Response: <value>" in
 * a gray box, and the scale item's exact wording behind an info tooltip.
 *
 * Three states, told apart honestly:
 *   - a response        -> the value
 *   - item unanswered   -> guardian "No response recorded" / school "School
 *                          response not received" (the CI's advisory-source
 *                          wording — the school not having come back is not a
 *                          missing record)
 *   - no SNAP instance  -> the whole block is omitted; an absent informant
 *                          claims nothing.
 *
 * When the informant used the optional example box behind a high rating,
 * their words appear under the response line as "Example given:", in the
 * same gray box. Nothing is rendered when there is no text — the response
 * line already says what the informant did or did not do, and an "(no
 * example given)" marker would be one more thing to read for no gain.
 */
function SnapEvidenceBlock({ source, dsmCode }) {
  const evidence = resolveCypSnapEvidence(source.snapInstance, dsmCode);
  if (!evidence) return null;

  const emptyText = source.role === 'schoolRep'
    ? 'School response not received'
    : 'No response recorded';

  return (
    <div className="flex flex-col gap-1">
      {/* The relationship in the label ("Parent/carer report (Mother)") can
          be longer than this narrow column. It wraps; the instrument badge
          stays one line beside it. */}
      <div className="flex items-start justify-between gap-2">
        <span className="text-slate-700 text-sm font-semibold min-w-0">{source.label}</span>
        <span className="px-2 py-0.5 bg-gray-200 rounded text-xs text-gray-600 whitespace-nowrap shrink-0">
          {source.scaleName}
        </span>
      </div>
      <div className="bg-gray-100 rounded-lg px-3 py-2 flex flex-col gap-1">
        <div className="flex items-center gap-2">
          {evidence.value ? (
            <span className="text-sm text-gray-800">
              <span className="font-semibold">Response:</span> {evidence.value}
            </span>
          ) : (
            <span className="text-sm text-gray-500 italic">{emptyText}</span>
          )}
          <InfoTooltip
            content={evidence.questionText}
            label={`${source.scaleName} item wording for ${dsmCode}`}
          />
        </div>
        {evidence.freeText && (
          <p className="text-sm text-gray-700 whitespace-pre-wrap">
            <span className="font-semibold">Example given:</span>{' '}
            {'\u201c'}{evidence.freeText}{'\u201d'}
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * What the clinician saw the child do during the Clinical Observation, if
 * they tagged a note to this symptom (cypAdhdFlow.mse ===
 * 'observationInSsi'). Sits in the same evidence column as the school's
 * SNAP-IV response, because that is what it is: a third account of the
 * symptom, this one first-hand.
 *
 * Read-only here, on purpose. The note belongs to the task it was written
 * on; re-wording or re-tagging it happens there, so one note never has two
 * places it can be edited.
 *
 * GROUPING BY TASK. Notes are grouped by the observation task they came
 * from (Scavenger hunt, Short story, etc.), matching how the adult autism
 * Criterion Mapping groups tagged notes by ADOS / Task Administration
 * source. Grouping is by task id, in the order each task is first seen,
 * so two notes from the same task share one heading even if something
 * else sat between them. Each group gets a small uppercase heading.
 *
 * EMPTY. When the phase exists but nothing was tagged to this symptom,
 * the panel stays, with one italic line saying so. Hiding it would look
 * like the column had failed to load. An org without the phase does not
 * render this block at all — that decision is the page's, via
 * showObservationEvidence, so Well Life Clinic never sees the line.
 */
function ObservationEvidenceBlock({ notes }) {
  const hasNotes = Array.isArray(notes) && notes.length > 0;

  // One heading per task, in the order the tasks first appear. A missing
  // task id stays in its own group so two incomplete notes are not merged
  // into a heading that does not name a real task.
  const groups = [];
  const groupIndexByTask = new Map();
  if (hasNotes) notes.forEach((note, index) => {
    const taskId = note.taskId || `note-${index}`;
    const existing = note.taskId ? groupIndexByTask.get(note.taskId) : undefined;
    if (existing !== undefined) {
      groups[existing].notes.push(note);
      return;
    }
    groupIndexByTask.set(taskId, groups.length);
    groups.push({
      taskId,
      taskTitle: note.taskTitle || 'Observation',
      notes: [note],
    });
  });

  return (
    <div className="flex flex-col gap-3" data-testid="observation-evidence">
      <div className="flex items-start justify-between gap-2">
        <span className="text-slate-700 text-sm font-semibold min-w-0">Clinical observation</span>
        <span className="px-2 py-0.5 bg-gray-200 rounded text-xs text-gray-600 whitespace-nowrap shrink-0">
          Clinician
        </span>
      </div>
      {hasNotes ? groups.map((group) => (
        <div key={group.taskId} className="flex flex-col gap-1.5">
          <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">
            {group.taskTitle}
          </p>
          <ul className="flex flex-col gap-2" aria-label={`Observation notes from ${group.taskTitle}`}>
            {group.notes.map((note, index) => (
              <li key={note.noteId || `${group.taskId}-${index}`} className="bg-gray-100 rounded-lg px-3 py-2">
                <span className="text-sm text-gray-800 whitespace-pre-wrap">{note.text}</span>
              </li>
            ))}
          </ul>
        </div>
      )) : (
        <p className="text-sm text-gray-500 italic">
          No notes from observation tagged to this symptom
        </p>
      )}
    </div>
  );
}

/**
 * Yes / No for whether the symptom is present in that time window.
 * Clicking the button that is already on clears it back to unanswered.
 * Inconclusive is no longer a choice. A record that already holds it still
 * shows the button, so that older answer stays visible and can be cleared.
 *
 * answerName is the question in the clinician's own words, for example
 * "Symptom present before age 12". A card can show up to three of these
 * pairs, and the visible button text is only "Yes" or "No". The accessible
 * name repeats the question so a screen reader can tell them apart.
 */
function DeterminationButtons({ value, onSelect, disabled, groupLabel, answerName }) {
  const base = 'min-h-[44px] min-w-[4.5rem] px-4 py-2 rounded-lg border border-gray-400 justify-center items-center gap-1 flex transition-all duration-300';
  const options = [
    { value: true, label: 'Yes', selectedClass: 'bg-green-200 border-gray-600 border-2' },
    { value: false, label: 'No', selectedClass: 'bg-red-200 border-gray-600 border-2' },
  ];
  if (value === CYP_SSI_INCONCLUSIVE) {
    options.push({
      value: CYP_SSI_INCONCLUSIVE,
      label: 'Inconclusive',
      selectedClass: 'bg-amber-100 border-gray-600 border-2',
    });
  }
  return (
    <div className="justify-start items-center gap-3 flex" role="group" aria-label={groupLabel}>
      {options.map((option) => {
        const isSelected = value === option.value;
        return (
          <button
            key={option.label}
            type="button"
            onClick={() => onSelect(isSelected ? null : option.value)}
            disabled={disabled}
            aria-pressed={isSelected}
            aria-label={answerName ? `${answerName}: ${option.label}` : option.label}
            className={`${base} ${isSelected ? option.selectedClass : 'bg-white'} ${disabled ? 'opacity-75 cursor-not-allowed' : ''}`}
          >
            <span className="text-slate-700 text-sm font-semibold leading-tight">{option.label}</span>
          </button>
        );
      })}
    </div>
  );
}

// The chips say Home and Education. The stored report labels stay
// "At home" and "At school" (CYP_SSI_SETTINGS) so existing sentences do
// not change wording.
const SETTING_CHIP_LABEL = {
  home: 'Home',
  education: 'Education',
};

const SETTINGS_TOOLTIP = 'Home is the child\'s home life. Education is school or college. Tick every setting where this symptom is seen. Both can be ticked.';

/**
 * A chip is on when the clinician has ticked that setting. A record made
 * when the setting was a Yes/No ("Met?") is still on if that answer was Yes
 * and the chip has not been set since.
 */
function isSettingSelected(stored, environment) {
  const explicit = (stored.settings || {})[environment.settingKey];
  if (explicit === true || explicit === false) return explicit === true;
  return (stored.environments || {})[environment.key]?.met === true;
}

/**
 * One example checklist. There is no notes box here, and no Current /
 * Primary school ticks under a ticked example: the symptom has one clinical
 * notes box under both lists.
 */
function ExampleColumn({
  environment,
  symptom,
  stored,
  examples,
  canEdit,
  onToggleExample,
}) {
  const selectedExamples = stored.selectedExamples || [];
  const chipLabel = SETTING_CHIP_LABEL[environment.key] || environment.title;

  if (examples.length === 0) return null;

  return (
    <div className="flex flex-col gap-1">
      <h3 className="text-slate-700 text-sm font-bold">
        {chipLabel}
      </h3>
      <div
        className="flex flex-col"
        role="group"
        aria-label={`${symptom.name} — ${environment.title.toLowerCase()} examples`}
      >
        {examples.map((example) => (
          <label key={example} className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              className="checkbox mt-1"
              checked={selectedExamples.includes(example)}
              onChange={() => onToggleExample(example)}
              disabled={!canEdit}
              aria-label={`Example: ${example}`}
            />
            <span className="text-slate-700 text-sm font-medium">{example}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

export default function CypAdhdSsiSymptomPanel({
  symptom,
  stored,
  canEdit,
  asksOnsetQuestion,
  // Onset-pair capture (TOC, TDI-205): before and after age 12 recorded as a
  // linked pair. asksAfterTwelveQuestion renders the after-12 decision;
  // onsetPairMode without it means the child has not yet turned 12, so the
  // after-12 half shows as not applicable instead of a question.
  asksAfterTwelveQuestion = false,
  onsetPairMode = false,
  childName = 'this child',
  notes,
  snapEvidenceSources = [],
  // Observation notes tagged to this symptom, from
  // collectCypObservationTaggedNotes. The panel is shown only when the org
  // runs the observation (showObservationEvidence). An empty list is then
  // the italic empty state, not a hidden column.
  observationNotes = [],
  showObservationEvidence = false,
  onFieldChange,
  onSettingToggle,
  onToggleEnvironmentExample,
  onNotesChange,
  // Notes-box identity and the cross-page Move catalogue. Without a domainId
  // the editor cannot tell a same-box reorder from a move out of the box.
  notesDomainId,
  noteMoveTargets,
  onMoveNote,
  index,
  total,
}) {
  const environments = stored.environments || {};
  // Ticks recorded against the single flat checklist this page had before the
  // October revision. Kept visible, read-only, so an earlier record is never
  // silently hidden; they also still reach the report drafting.
  const earlierExamples = stored.selectedExamples || [];
  // Settings describe where a present symptom is seen, so they stay dimmed
  // until the six-month answer is Yes. Same rule as the adult SSI.
  const settingsOpen = stored.persistedSixMonths === true;

  return (
    <div className="bg-white p-6 rounded-lg mb-6">
      <div className="flex items-center justify-start gap-2 mb-1">
        <span className="text-gray-400 text-sm font-semibold">
          {symptom.dsmCode} · {index + 1} of {total}
        </span>
      </div>
      <div className="flex items-center justify-start gap-2 mb-3">
        <h2 className="flex items-center gap-2 text-gray-900 text-xl font-semibold leading-9 break-words text-left">
          {symptom.description}
          {/* Clinical guidance, rendered only when content exists so the page
              carries no empty affordance while it is pending. */}
          {symptom.tooltip && (
            <InfoTooltip
              content={symptom.tooltip}
              label={`Clinical guidance for ${symptom.name}`}
            />
          )}
        </h2>
      </div>

      <div className="flex mb-4">
        <div className="w-full text-left">
          <div className="w-full p-6 bg-gray-50 rounded-2xl border border-gray-200 flex flex-col gap-2">
            {/* One row, wrapping only when the window is too narrow for both
                questions. Stacking them pushed the notes box below the fold
                on a shorter screen. Onset sits on the left; the six-month
                decision is always the rightmost item, including when onset
                is not asked. */}
            <div className="flex flex-wrap items-center justify-end gap-x-6 gap-y-2">
              {asksOnsetQuestion && (
                <div className="flex items-center gap-3">
                  <span className="text-slate-700 text-base font-medium">
                    Symptom present before age 12
                  </span>
                  <DeterminationButtons
                    value={stored.presentBeforeTwelve ?? null}
                    onSelect={(next) => onFieldChange('presentBeforeTwelve', next)}
                    disabled={!canEdit}
                    groupLabel={`${symptom.name} — present before age 12?`}
                    answerName="Symptom present before age 12"
                  />
                </div>
              )}

              {asksAfterTwelveQuestion && (
                <div className="flex items-center gap-3">
                  <span className="text-slate-700 text-base font-medium">
                    Symptom present after age 12
                  </span>
                  <DeterminationButtons
                    value={stored.presentAfterTwelve ?? null}
                    onSelect={(next) => onFieldChange('presentAfterTwelve', next)}
                    disabled={!canEdit}
                    groupLabel={`${symptom.name} — present after age 12?`}
                    answerName="Symptom present after age 12"
                  />
                </div>
              )}
              {onsetPairMode && !asksAfterTwelveQuestion && (
                <div className="flex items-center gap-3">
                  <span className="text-slate-700 text-base font-medium">
                    Symptom present after age 12
                  </span>
                  {/* A child who has not yet turned 12 has no after-12
                      period, so nothing is asked or stored. */}
                  <p className="text-sm text-gray-600">
                    Not applicable — {childName} has not yet turned 12.
                  </p>
                </div>
              )}

              <div className="flex items-center gap-3">
                <span className="text-slate-700 text-base font-medium">
                  Symptom has persisted for at least 6 months
                </span>
                <DeterminationButtons
                  value={stored.persistedSixMonths ?? null}
                  onSelect={(next) => onFieldChange('persistedSixMonths', next)}
                  disabled={!canEdit}
                  groupLabel={`${symptom.name} — persisted for at least 6 months?`}
                  answerName="Symptom has persisted for at least 6 months"
                />
              </div>
            </div>

            <div className="flex w-full gap-6 items-start mt-4">
              {/* LEFT: the school's SNAP-IV response. The parent/carer's
                  response is filtered out before it reaches this panel.
                  This column stays even when nothing has come back, so the
                  examples never slide over into it.
                  Adult ADHD gives its rating-scale column a third of the
                  card, because that column also shows the scale question.
                  A SNAP answer is one short rating, and the right side here
                  holds two columns of examples, so a quarter leaves the
                  decisions the room. min-w-0 stops a long relationship
                  label from forcing the column wider than that quarter. */}
              <div className="w-1/4 min-w-0 flex flex-col gap-3">
                {snapEvidenceSources.map((source) => (
                  <SnapEvidenceBlock
                    key={source.uid}
                    source={source}
                    dsmCode={symptom.dsmCode}
                  />
                ))}

                {/* First-hand evidence under the school's, separated by the
                    same hairline the prompts use below. Shown for every
                    symptom when the org runs the observation, including
                    when nothing was tagged to this one. */}
                {showObservationEvidence && (
                  <div className={snapEvidenceSources.length > 0 ? 'border-t border-gray-200 pt-4' : ''}>
                    <ObservationEvidenceBlock notes={observationNotes} />
                  </div>
                )}

                {SHOW_SUGGESTED_PROMPTS && symptom.suggestedPrompts?.length > 0 && (
                  <div className={(snapEvidenceSources.length > 0 || observationNotes.length > 0) ? 'border-t border-gray-200 pt-4' : ''}>
                    <h3 className="text-slate-700 text-sm font-bold mb-2">
                      Suggested prompts
                    </h3>
                    <ul className="list-disc pl-5 space-y-1">
                      {symptom.suggestedPrompts.map((prompt) => (
                        <li key={prompt} className="text-sm text-gray-600">{prompt}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>

              <div className="border-l border-gray-200 self-stretch"></div>

              <div className="min-w-0 flex-1 flex flex-col">
                <div
                  className="transition-opacity duration-300"
                  style={{ opacity: settingsOpen ? 1 : 0.5 }}
                >
                  <div className="flex items-center gap-1 mb-3">
                    <h3 className="text-slate-700 text-sm font-bold">
                      Settings in which this symptom occurs
                    </h3>
                    <InfoTooltip
                      content={SETTINGS_TOOLTIP}
                      label="More information about settings in which this symptom occurs"
                      maxWidth="380px"
                    />
                  </div>
                  <div className="flex flex-wrap gap-2" role="group" aria-label="Settings in which this symptom occurs">
                    {CYP_SSI_ENVIRONMENTS.map((environment) => {
                      const selected = isSettingSelected(stored, environment);
                      const label = SETTING_CHIP_LABEL[environment.key];
                      return (
                        <button
                          key={environment.key}
                          type="button"
                          onClick={() => onSettingToggle(environment.key)}
                          disabled={!settingsOpen || !canEdit}
                          aria-pressed={selected}
                          className={`min-h-[44px] px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${
                            !settingsOpen || !canEdit
                              ? 'cursor-not-allowed bg-gray-100 text-gray-400 border border-gray-300'
                              : selected
                                ? 'bg-violet-300 border-2 border-violet-500 text-violet-900'
                                : 'bg-violet-100 hover:bg-violet-200 border border-violet-200 text-gray-700'
                          }`}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="border-t border-gray-200 my-4"></div>

                <div className="flex flex-col gap-2">
                  <span className="text-slate-700 text-sm font-semibold">Examples</span>
                  <div className="grid grid-cols-2 gap-6">
                    {CYP_SSI_ENVIRONMENTS.map((environment) => (
                      <ExampleColumn
                        key={environment.key}
                        environment={environment}
                        symptom={symptom}
                        stored={environments[environment.key] || {}}
                        examples={symptom[environment.examplesField] || []}
                        canEdit={canEdit}
                        onToggleExample={(example) => onToggleEnvironmentExample(environment.key, example)}
                      />
                    ))}
                  </div>
                  {symptom.examplesFootnote && (
                    <p className="text-sm text-gray-600">{symptom.examplesFootnote}</p>
                  )}
                </div>

                {earlierExamples.length > 0 && (
                  <div className="mt-4">
                    <h3 className="text-slate-700 text-sm font-bold mb-1">
                      Examples recorded before the checklist was split by environment
                    </h3>
                    <ul className="list-disc pl-5 space-y-1">
                      {earlierExamples.map((example) => (
                        <li key={example} className="text-sm text-gray-700">{example}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="border-t border-gray-200 my-4"></div>

                <div className="flex flex-col gap-2">
                  <label className="text-slate-700 text-sm font-semibold">
                    Clinical Notes
                  </label>
                  <StructuredNotesEditor
                    notes={notes}
                    onChange={onNotesChange}
                    domainId={notesDomainId}
                    onMoveNoteIn={onMoveNote}
                    moveTargets={noteMoveTargets}
                    currentMoveId={notesDomainId}
                    onMoveNoteTo={onMoveNote}
                    placeholder="Add a note, then press Enter to save each point"
                    enableCriteriaTagging={CYP_NOTE_TAGGING_ENABLED}
                    tagOptions={CYP_NOTE_TAG_OPTIONS}
                    readOnly={!canEdit}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
